<template>
  <input
    ref="numeric"
    v-model="amount"
    :placeholder="placeholder"
    type="tel"
    aria-required="true"
    class="form-control"
    @blur="onBlurHandler"
    @input="onInputHandler"
    @focus="onFocusHandler"
  />
</template>

<script>
import accounting from "accounting-js";

export default {
  name: "FormInputNumeric",

  props: {
    /**
     * Input placeholder.
     */
    placeholder: {
      type: String,
      default: "",
      required: false,
    },

    /**
     * Value when the input is empty
     */
    emptyValue: {
      type: [Number, String],
      default: "",
      required: false,
    },

    /**
     * Number of decimals.
     * Decimals symbol are the opposite of separator symbol.
     */
    precision: {
      type: Number,
      default: 4,
      required: false,
    },

    /**
     * Thousand separator type.
     * Separator props accept either . or , (default).
     */
    separator: {
      type: String,
      default: ",",
      required: false,
    },

    /**
     * Forced thousand separator.
     * Accepts any string.
     */
    thousandSeparator: {
      default: undefined,
      required: false,
      type: String,
    },

    /**
     * Forced decimal separator.
     * Accepts any string.
     */
    decimalSeparator: {
      default: undefined,
      required: false,
      type: String,
    },

    /**
     * The output type used for v-model.
     * It can either be String or Number (default).
     */
    outputType: {
      required: false,
      type: String,
      default: "Number",
    },

    /**
     * v-model value.
     */
    value: {
      type: [Number, String],
      default: "",
      required: false,
    },
  },

  data: () => ({
    amount: "",
  }),

  computed: {
    /**
     * Number type of formatted value.
     * @return {Number}
     */
    amountNumber() {
      if (!this.validValue(this.amount)) {
        return "";
      }
      return this.unformat(this.amount);
    },

    /**
     * Number type of value props.
     * @return {Number}
     */
    valueNumber() {
      if (!this.validValue(this.value)) {
        return "";
      }
      return this.$refs.numeric === document.activeElement
        ? this.unformat(
            this.amountNumber !== 0 && this.amountNumber !== this.value
              ? this.amountNumber
              : this.value
          )
        : this.unformat(this.value);
    },

    /**
     * Define decimal separator based on separator props.
     * @return {String} '.' or ','
     */
    decimalSeparatorSymbol() {
      if (typeof this.decimalSeparator !== "undefined") {
        return this.decimalSeparator;
      }
      if (this.separator === ",") {
        return ".";
      }
      return ",";
    },

    /**
     * Define thousand separator based on separator props.
     * @return {String} '.' or ','
     */
    thousandSeparatorSymbol() {
      if (typeof this.thousandSeparator !== "undefined") {
        return this.thousandSeparator;
      }
      if (this.separator === ".") {
        return ".";
      }
      if (this.separator === "space") {
        return " ";
      }
      return ",";
    },
  },

  watch: {
    /**
     * Watch for value change from other input with same v-model.
     * @param {Number} newValue
     */
    valueNumber(newValue) {
      if (this.$refs.numeric !== document.activeElement) {
        this.amount = this.format(newValue);
      }
    },

    /**
     * Immediately reflect separator changes
     */
    separator() {
      this.amount = this.format(this.valueNumber);
    },

    /**
     * Immediately reflect precision changes
     */
    precision() {
      this.amount = this.format(this.valueNumber);
    },
  },

  mounted() {
    // Set default value props when placeholder undefined.
    if (!this.placeholder) {
      this.amount = this.format(this.valueNumber);

      // In case of delayed props value.
      setTimeout(() => {
        this.amount = this.format(this.valueNumber);
      }, 500);
    }
  },

  methods: {
    /**
     * Count decimals in a value.
     * @param {Number} value
     */
    countDecimals(value) {
      if (Math.floor(value) === value) {
        return 0;
      }
      return value.toString().split(".")[1].length || 0;
    },

    /**
     * Handle blur event.
     * @param {Object} e
     */
    onBlurHandler(e) {
      this.$emit("blur", e);
      this.amount = this.format(this.valueNumber);
    },

    /**
     * Handle focus event.
     * @param {Object} e
     */
    onFocusHandler(e) {
      this.$emit("focus", e);
      if (!this.validValue(this.valueNumber)) {
        this.amount = "";
      } else {
        this.amount = accounting.formatNumber(this.valueNumber, {
          symbol: "",
          format: "%v",
          precision: Number(
            this.countDecimals(this.valueNumber) ? this.precision : 0
          ),
          decimal: this.decimalSeparatorSymbol,
          thousand: "",
        });
      }
    },

    /**
     * Handle input event.
     */
    onInputHandler() {
      this.update(this.amountNumber);
    },

    /**
     * Update parent component model value.
     * @param {Number} value
     */
    update(value) {
      if (!this.validValue(value)) {
        this.$emit("input", "");
        return;
      }
      const fixedValue = accounting.toFixed(value, this.precision);
      const output =
        this.outputType.toLowerCase() === "string"
          ? fixedValue
          : Number(fixedValue);
      this.$emit("input", output);
    },

    /**
     * Format value using symbol and separator.
     * @param {Number} value
     * @return {String}
     */
    format(value) {
      if (!this.validValue(value)) {
        return "";
      }
      return accounting.formatNumber(value, {
        symbol: "",
        format: "%v",
        precision: Number(this.countDecimals(value) ? this.precision : 0),
        decimal: this.decimalSeparatorSymbol,
        thousand: this.thousandSeparatorSymbol,
      });
    },

    /**
     * Remove symbol and separator.
     * @param {Number} value
     * @return {Number}
     */
    unformat(value) {
      const toUnformat =
        typeof value === "string" && value === "" ? this.emptyValue : value;
      return accounting.unformat(toUnformat, this.decimalSeparatorSymbol);
    },
    /**
     * Returns if a value is valid for number formatting.
     * @param {Number} value
     * @return {Boolean}
     */
    validValue(value) {
      if (value === "" || value === null || value === undefined) {
        return false;
      }

      return true;
    },
  },
};
</script>
